import axios, {AxiosProgressEvent, AxiosRequestConfig, InternalAxiosRequestConfig} from 'axios';
import {useEffect, useMemo, useState} from 'react';
import {TokenService} from 'services';
import {errorModal} from 'utils/errorModal';

export const API_URL = process.env.REACT_APP_API_URL;
export const axiosInstance = axios.create({
    // withCredentials : true,
    baseURL: API_URL,
});

interface APIServiceConfigOptions {
    voidLoadingOverlay?: boolean;
    progressCalculator?: (progressEvent: AxiosProgressEvent) => number;
}

export interface APIServiceAxiosRequestConfig extends AxiosRequestConfig, APIServiceConfigOptions {
}

interface APIServiceInternalAxiosRequestConfig extends InternalAxiosRequestConfig, APIServiceConfigOptions {
}

axiosInstance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig<any>) => {
        if (config.url?.substring(0, 6) !== '/token') {
            return await (new TokenService()).getToken().then(token => {
                config.headers.Authorization = `Bearer ${token.jwt.replace('"', '')}`;
                return config;
            });
        }
        return config;
    },
    error => error
);

let refreshTokenPromise: Promise<any>|null = null;// this holds any in-progress token refresh requests

axiosInstance.interceptors.response.use(r => r, async error => {
    if (!error.response) {
        console.error('axiosInstance.interceptors.response says: \'response was empty\'');
        return Promise.reject(error);
    }

    if (error.config.url === '/token/reCreate') {
        console.error('token reacreation failed');
        return Promise.reject(error);
    }
    if (error.config && error.response.status === 401) {

        if (!refreshTokenPromise) { // check for an existing in-progress request
            // if nothing is in-progress, start a new refresh token request
            refreshTokenPromise = (new TokenService()).reCreate().then(token => {
                TokenService.store(token);
                refreshTokenPromise = null; // clear state
                return token; // resolve with the new token
            });
        }

        const token_2 = await refreshTokenPromise;
        error.config.headers.Authorization = `Bearer ${token_2.jwt.replace('"', '')}`;
        return await axiosInstance.request(error.config);
    }
    console.error(`axios.request.error: ${error.config.url}`, error.response);
    errorModal(error);
    return Promise.reject(error);
});


export const useAxiosLoader = () => {
    const [counter, setCounter] = useState(0);
    const [progress, setProgress] = useState(0);

    const interceptors = useMemo(() => {
        const inc = (from: string = 'none') => {
            setCounter(counter + 1);
            //console.log(`inc :${from}`, counter);
        };
        const dec = (from: string = 'none') => {
            let newValue = counter - 1;
            if (newValue < 0) {
                newValue = 0;
            }
            setCounter(newValue);
            //console.log(`dec :${from}`, counter);
        };

        return ({
            request: (config: APIServiceInternalAxiosRequestConfig) => {
                if (config?.voidLoadingOverlay === true) {
                    setCounter(0);
                    setProgress(0);
                    return config;
                }
                inc('request');
                config.onUploadProgress = (progressEvent: AxiosProgressEvent) => {
                    let progress = 0;
                    if (config.progressCalculator) {
                        progress = config.progressCalculator(progressEvent);
                    }
                    else {
                        if (typeof progressEvent.total === 'undefined') {
                            progress = 1;
                        }
                        else {
                            progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        }
                    }
                    setProgress(progress);
                };
                return config;
            },
            response: (response: any) => {
                dec('response');
                return response;
            },
            error: (error: any) => {
                dec('error');
                return Promise.reject(error);
            },
        });
    }, []); // create the interceptors

    useEffect(() => {
        // add request interceptors
        const reqInterceptor = axiosInstance.interceptors.request.use(interceptors.request, interceptors.error);
        // add response interceptors
        const resInterceptor = axiosInstance.interceptors.response.use(interceptors.response, interceptors.error);
        return () => {
            // remove all intercepts when done
            axiosInstance.interceptors.request.eject(reqInterceptor);
            axiosInstance.interceptors.response.eject(resInterceptor);
        };
    }, [interceptors]);

    return [counter > 0, progress];
};
